import { useEffect, useState, useContext, useMemo, useCallback, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import 'swiper/css';
import classes from './Main.module.scss';
import { GeneralContext } from '../../context/generalContext';
import { addressesAPI, domainAPI } from '../../services/GeneralService';
import featureIcon1 from '../../images/mp-feature-icon-1.svg';
import featureIcon2 from '../../images/mp-feature-icon-2.svg';
import featureIcon3 from '../../images/mp-feature-icon-3.svg';
import featureIcon4 from '../../images/mp-feature-icon-4.svg';
import reserveImg from '../../images/mp-reserve-img.webp';
import reserveImgTashkent from '../../images/mp-reserve-img-tashkent.webp';
import MainClubSection from './MainClubSection/MainClubSection';
import { getSeasonFormDate } from './lib/getSeasonFormDate';
import { getBaseUrl } from '../../functions/getBaseUrl';
import SkeletonMainTopSlider from '../../Components/Skeleton/SkeletonMainTopSlider/SkeletonMainTopSlider';
import SkeletonMainCategoriesSlider from '../../Components/Skeleton/SkeletonMainCategoriesSlider/SkeletonMainCategoriesSlider';
import SkeletonMainAboutSlider from '../../Components/Skeleton/SkeletonMainAboutSlider/SkeletonMainAboutSlider';
import SkeletonMainSeasonalSlider from '../../Components/Skeleton/SkeletonMainSeasonalSlider/SkeletonMainSeasonalSlider';
import Skeleton from '../../Components/Skeleton/Skeleton';

const MainTopSlider = lazy(() => import('./MainTopSlider/MainTopSlider'));
const MainCategoriesSlider = lazy(() => import('./MainCategoriesSlider/MainCategoriesSlider'));
const MainAboutSlider = lazy(() => import('./MainAboutSlider/MainAboutSlider'));
const MainSeasonalSlider = lazy(() => import('./MainSeasonalSlider/MainSeasonalSlider'));
const YandexMap = lazy(() => import('../../Components/YandexMap/YandexMap'));
const MainAboutModal = lazy(() => import('./MainAboutModal/MainAboutModal'));

const goToTop = () => {
  window.scrollTo({
    top: 0,
  });
};

export default function Main() {
  const { activeLanguage, activeCity, currentCityAddress, selectedAddress, subdomainIsActive, subdomain, cities } =
    useContext(GeneralContext);
  const { data: addresses } = addressesAPI.useFetchAllAddressesQuery();
  const { data: domainData } = domainAPI.useFetchAllDomainQuery();
  const currAddress = useMemo(() => addresses?.find(el => el.id === selectedAddress), [addresses, selectedAddress]);
  const addressesFilteredByCity = useMemo(
    () => addresses?.filter(address => address.cityId === activeCity) || [],
    [addresses, activeCity],
  );
  // для слайдера "о нас"
  const [description, setDescription] = useState([]);
  useEffect(() => {
    const apiUrl = `${getBaseUrl()}/api/places-slider`;
    axios.get(apiUrl).then(resp => {
      const descrData = resp.data;
      setDescription(descrData);
    });
  }, []);

  const [descriptionShort, setDescriptionShort] = useState();
  useEffect(() => {
    const text = document.createElement('div');
    description
      ? (text.innerHTML = activeLanguage === 1 ? description[0]?.main_text : description[0]?.main_text_en) // текст из апи
      : (text.innerHTM = '');

    let totalLength = 0; // подсчет общего кол-ва знаков
    const limit = 450; // лимит знаков
    const result = document.createElement('div'); // текст для вставки
    for (let i = 0; i < text.children.length; i += 1) {
      if (text.children[i].innerText.length + totalLength <= 450) {
        // если элемент полностью входит
        const element = document.createElement(`${text.children[i].localName}`);
        element.innerHTML = text.children[i].innerHTML;
        result.appendChild(element);

        totalLength += element.innerText.length;
      } else {
        // если не помещается полностью - обрезаем согласно лимиту + последнее слово если обрезано - не берем
        const smallLimit = limit - totalLength;
        const element = document.createElement(`${text.children[i].localName}`);
        element.innerHTML = text.children[i].innerText.slice(0, smallLimit).split(' ').slice(0, -1).join(' ');
        result.appendChild(element);

        totalLength += element.innerText.length;
        break;
      }
    }
    setDescriptionShort(result);
  }, [description, activeLanguage]);

  const [isDescrOpened, setIsDescrOpened] = useState(false);
  const [isDescrOpenedModal, setIsDescrOpenedModal] = useState(false);

  const openDescr = useCallback(() => {
    window.innerWidth > 991 ? setIsDescrOpenedModal(true) : setIsDescrOpened(true);
  }, []);

  const closeDescr = useCallback(() => {
    if (window.innerWidth > 991) {
      setIsDescrOpenedModal(false);
    } else {
      setIsDescrOpened(false);
      window.scrollTo(0, 1500);
    }
  }, []);

  function declOfNum(number, titles) {
    const cases = [2, 0, 1, 1, 1, 2];
    return titles[number % 100 > 4 && number % 100 < 20 ? 2 : cases[number % 10 < 5 ? number % 10 : 5]];
  }

  const [mail, setMail] = useState('info@lambic.ru');
  useEffect(() => {
    setMail(cities.filter(city => city.id === activeCity)[0]?.email || 'info@lambic.ru');
  }, [activeCity]);

  const [balloonAddress, setBalloonAddress] = useState('');
  const handleClick = () => {
    setBalloonAddress(document.querySelector('.custom-balloon__title')?.textContent || '');
  };
  // ymaps-2-1-79-placemark-overlay ymaps-2-1-79-user-selection-none
  useEffect(() => {
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  });

  const langMapper = {
    restaurants: [
      '',
      `${declOfNum(addresses?.length, ['Открыт', 'Открыты', 'Открыты'])} ${addresses?.length} ${declOfNum(
        addresses?.length,
        ['ресторан', 'ресторана', 'ресторанов'],
      )}`,
      `${addresses?.length} restaurants open`,
    ],
    menu: ['', 'Авторское меню', "Author's menu"],
    lunch: ['', 'Бизнес-ланчи от 660\u00A0\u20bd', 'Business lunches from 660\u00A0\u20bd'],
    lunchNovosib: ['', 'Бизнес-ланчи от 590\u00A0\u20bd', 'Business lunches from 590\u00A0\u20bd'],
    beer: ['', '160 сортов пива', '160 beers'],
    bar: ['', 'Широкая барная карта', 'Rich bar list'],
    birthday: ['', 'Скидка именинникам', 'Birthday guests discount'],
  };
  const restaurantsText = langMapper?.restaurants[activeLanguage];
  const menuTextOther =
    activeCity === 4 || activeCity === 6 ? langMapper?.menu[activeLanguage] : langMapper?.lunch[activeLanguage];
  const menuText = activeCity === 2 ? langMapper?.lunchNovosib[activeLanguage] : menuTextOther;
  const beerText = activeCity === 6 ? langMapper?.bar[activeLanguage] : langMapper?.beer[activeLanguage];
  const birthdayText = langMapper?.birthday[activeLanguage];
  const featureItems = [
    { id: 1, icon: featureIcon1, text: restaurantsText },
    { id: 2, icon: featureIcon2, text: menuText },
    { id: 3, icon: featureIcon3, text: beerText },
    { id: 4, icon: featureIcon4, text: birthdayText },
  ];

  return (
    <div className={classes.mpWrap}>
      <Helmet>
        {subdomainIsActive ? (
          <title>
            {activeLanguage === 1
              ? domainData?.title_tag_ru || 'Brasserie Lambic - Ресторан великолепного пива из Бельгии'
              : domainData?.title_tag_en || 'Brasserie Lambic - A great beer restaurant from Belgium'}
          </title>
        ) : (
          <title>
            {activeLanguage === 1
              ? 'Brasserie Lambic - Ресторан великолепного пива из Бельгии'
              : 'Brasserie Lambic - A great beer restaurant from Belgium'}
          </title>
        )}
      </Helmet>

      <div className={classes.mpContainer}>
        <Suspense fallback={<SkeletonMainTopSlider />}>
          <MainTopSlider />
        </Suspense>
        {subdomain !== 'alt' && <MainClubSection />}

        <section className={classes.menuSection}>
          <div className={classes.menuHeadRow}>
            <div className={classes.h2Title}>
              {activeLanguage === 1 ? 'Авторское' : "Author's"}
              <br />
              {activeLanguage === 1 ? 'меню' : 'menu'}{' '}
            </div>
            <div className={classes.menuHeadInfo}>
              {activeLanguage === 1 ? 'Европейская и бельгийская кухня' : 'European and Belgian cuisine'}
              <br />{' '}
              {activeLanguage === 1
                ? 'в исполнении поваров Brasserie Lambic'
                : 'in the performance of chefs Brasserie Lambic'}
            </div>
            <div className={classes.menuHeadBtn}>
              <Link className={classes.btn} to={activeLanguage === 1 ? '/menu' : '/en/menu'}>
                {activeLanguage === 1 ? 'Смотреть меню' : 'See menu'}
              </Link>
            </div>
          </div>
          <Suspense fallback={<SkeletonMainCategoriesSlider />}>
            <MainCategoriesSlider />
          </Suspense>
        </section>

        <section
          className={`${classes.aboutSection} ${classes.fullWidthCont} ${
            isDescrOpened ? classes.aboutSectionOpened : ''
          }`}
        >
          <div className={`${classes.h2Title} ${classes.onlyMobile}`}>
            {activeLanguage === 1 ? 'О нас' : 'About us'}
          </div>
          <Suspense fallback={<SkeletonMainAboutSlider />}>
            <MainAboutSlider />
          </Suspense>
          <div className={`${classes.aboutContent} ${isDescrOpened ? classes.aboutContentOpened : ''}`}>
            {window.innerWidth > 991 ? (
              <p
                className={`${classes.aboutDescription} ${isDescrOpened ? classes.aboutDescriptionOpened : ''}`}
                dangerouslySetInnerHTML={{ __html: descriptionShort?.innerHTML }}
              />
            ) : (
              <>
                <p
                  className={`${classes.aboutDescription} ${
                    isDescrOpened ? classes.disabled : classes.aboutDescriptionOpened
                  }`}
                  dangerouslySetInnerHTML={{ __html: descriptionShort?.innerHTML }}
                />
                {description && (
                  <p
                    className={`${classes.aboutDescription} ${
                      isDescrOpened ? classes.aboutDescriptionOpened : classes.disabled
                    }`}
                    dangerouslySetInnerHTML={{
                      __html: activeLanguage === 1 ? description[0]?.main_text : description[0]?.main_text_en,
                    }}
                  />
                )}
              </>
            )}
            <div className={isDescrOpened ? classes.disabled : classes.readMore} onClick={() => openDescr()}>
              {activeLanguage === 1 ? 'Читать полностью' : 'Read more'}
            </div>
            <div className={isDescrOpened ? classes.readMore : classes.disabled} onClick={() => closeDescr()}>
              {activeLanguage === 1 ? 'Свернуть' : 'Hide'}
            </div>
          </div>
        </section>
        <MainAboutModal
          modalActive={isDescrOpenedModal}
          setModalActive={setIsDescrOpenedModal}
          subdomainIsActive={subdomainIsActive}
          description={description}
        />

        <div className={classes.featuresBox}>
          <div className={`${classes.h2Title} ${classes.onlyMobile}`}>
            {activeLanguage === 1 ? 'Факты о Lambic' : 'Facts about Lambic'}
          </div>
          <div className={classes.featuresList}>
            {featureItems?.map(item => (
              <div className={classes.featureItem} key={item.id}>
                <div className={classes.featureItemIcon}>
                  <img src={item.icon} alt='' />
                </div>
                <div className={classes.featureItemName}>{item.text}</div>
              </div>
            ))}
          </div>
        </div>

        {window.innerWidth > 991 && (
          <section className={classes.seasonalSection}>
            <div className={classes.seasonalHeadRow}>
              <div className={classes.h2Title}>
                {activeLanguage === 1 ? 'Сезонные' : 'Seasonal'}
                <br />
                {activeLanguage === 1 ? 'предложения' : 'menu'}{' '}
              </div>
              {activeLanguage === 1 ? (
                <div className={classes.seasonalHeadInfo}>
                  {getSeasonFormDate(new Date())?.text || 'Сейчас'}&nbsp;&mdash; время теплых и&nbsp;уютных встреч.
                  В&nbsp;Brasserie Lambic вас ждет не&nbsp;только приятная атмосфера, но&nbsp;и&nbsp;специальное
                  сезонное меню из&nbsp;европейской и&nbsp;бельгийской кухни.
                </div>
              ) : (
                <div className={classes.seasonalHeadInfo}>
                  {getSeasonFormDate(new Date())?.text_en || 'Now'} is&nbsp;the time of&nbsp;warm and cozy meetings.
                  In&nbsp;Brasserie Lambic you will find not only a&nbsp;pleasant atmosphere but also a&nbsp;special
                  seasonal menu of&nbsp;European and Belgian cuisine.
                </div>
              )}
              <div className={classes.seasonalHeadBtn}>
                <Link className={classes.btn} to={activeLanguage === 1 ? '/menu' : '/en/menu'} onClick={goToTop}>
                  {activeLanguage === 1 ? 'Смотреть все меню' : 'See all menu'}
                </Link>
              </div>
            </div>
            <Suspense fallback={<SkeletonMainSeasonalSlider />}>
              <MainSeasonalSlider />
            </Suspense>
          </section>
        )}

        <section className={`${classes.reserveSection} ${classes.fullWidthCont}`}>
          <div className={classes.container}>
            <div className={classes.h2Title}>
              {activeLanguage === 1 ? 'Забронируйте стол прямо сейчас' : 'Reserve a table now'}
            </div>
            <div className={classes.reserveBtnBox}>
              <Link
                className={`${classes.btn} ${classes.btnBlack}`}
                to={activeLanguage === 1 ? '/order' : '/en/order'}
                onClick={goToTop}
              >
                {activeLanguage === 1 ? 'Забронировать стол' : 'Reserve a table'}
              </Link>
            </div>
          </div>
          <img className={classes.reserveImg} src={activeCity === 6 ? reserveImgTashkent : reserveImg} alt='' />
        </section>

        <section className={`${classes.addressesSection} ${classes.fullWidthCont}`}>
          <div className={classes.addressesMapBox}>
            <Suspense fallback={<Skeleton borderRadius='0px' width='100%' height='100%' isLight={false} />}>
              <YandexMap addresses={addresses} />
            </Suspense>
          </div>
          <div itemScope itemType='https://schema.org/Organization' className={classes.addressesContent}>
            <div className={classes.h2Title}>
              {activeLanguage === 1 && (
                <>
                  {subdomainIsActive ? 'Адрес реcторана ' : 'Адреса реcторанов '}
                  <span itemProp='name'>Brasserie Lambic</span>
                </>
              )}
              {activeLanguage !== 1 && (
                <>
                  <span itemProp='name'>Brasserie Lambic</span>
                  {subdomainIsActive ? ' address' : ' addresses'}
                </>
              )}
            </div>
            <div className={classes.addressesContacts}>
              <div className={classes.addressesContact}>
                <div className={classes.addressesLabel}>
                  {subdomainIsActive ? (
                    <>{activeLanguage === 1 ? 'Контактный телефон' : 'Phone'}</>
                  ) : (
                    <>{activeLanguage === 1 ? 'Единый телефон сети' : 'Public network phone'}</>
                  )}
                </div>
                <div>
                  {currentCityAddress?.tel && (
                    <a href={`tel:${currentCityAddress.tel}`}>
                      <span itemProp='telephone'>{currentCityAddress.telModified}</span>
                    </a>
                  )}
                </div>
              </div>
              <div className={classes.addressesContact}>
                {subdomainIsActive &&
                  activeCity !== 4 &&
                  activeCity !== 1 &&
                  addressesFilteredByCity?.[0]?.hours?.length > 0 && (
                    <>
                      <div className={classes.addressesLabel}>
                        {activeLanguage === 1 ? 'Прием звонков' : 'Accepting calls'}
                      </div>
                      {activeLanguage === 1 ? (
                        <>
                          {addressesFilteredByCity?.[0]?.hours.map((hour, index) => (
                            <div key={index}>{hour}</div>
                          ))}
                        </>
                      ) : (
                        <>
                          {addressesFilteredByCity?.[0]?.hours_en.map((hour, index) => (
                            <div key={index}>{hour}</div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                {subdomainIsActive &&
                  activeCity === 1 &&
                  addresses?.filter(address => address.link?.split('.')[0] === subdomain)[0]?.hours?.length > 0 && (
                    <>
                      <div className={classes.addressesLabel}>
                        {activeLanguage === 1 ? 'Прием звонков' : 'Accepting calls'}
                      </div>
                      {activeLanguage === 1 ? (
                        <>
                          {addresses
                            ?.filter(address => address.link?.split('.')[0] === subdomain)[0]
                            .hours.map((hour, index) => (
                              <div key={index}>{hour}</div>
                            ))}
                        </>
                      ) : (
                        addresses
                          ?.filter(address => address.link?.split('.')[0] === subdomain)[0]
                          .hours_en.map((hour, index) => <div key={index}>{hour}</div>)
                      )}
                    </>
                  )}
                {!subdomainIsActive && (
                  <>
                    <div className={classes.addressesLabel}>
                      {activeLanguage === 1 ? 'Прием звонков' : 'Accepting calls'}
                    </div>
                    <div>
                      {activeLanguage === 1 ? 'Пн—Чт, Вс с 12:00 до 00:00' : 'Mon—Thu, Sun from 12 PM to 12 AM'}
                    </div>
                    <div>{activeLanguage === 1 ? 'Пт с 12:00 до 03:00' : 'Fri from 12 PM to 3 AM'}</div>
                    <div>{activeLanguage === 1 ? 'Сб с 12:00 до 01:00' : 'Sat from 12 PM to 1 AM'}</div>
                  </>
                )}
                {activeCity === 4 && (
                  <>
                    <div className={classes.addressesLabel}>
                      {activeLanguage === 1 ? 'Прием звонков' : 'Accepting calls'}
                    </div>
                    <div>{activeLanguage === 1 ? 'с 12:00 до 00:00' : 'from 12 PM to 12 AM'}</div>
                  </>
                )}
              </div>
              <div className={classes.addressesContact}>
                <div className={classes.addressesLabel}>{activeLanguage === 1 ? 'Почта' : 'Email'}</div>
                <div>
                  <a href={`mailto:${mail}`}>
                    <span itemProp='email'>{mail}</span>
                  </a>
                </div>
              </div>
              {balloonAddress && !subdomainIsActive && (
                <div className={classes.addressesContact} id='opened-balloon-title'>
                  <div className={classes.addressesLabel}>
                    {activeLanguage === 1 ? 'Адрес ресторана' : 'Brasserie Lambic address'}
                  </div>
                  <div>{balloonAddress}</div>
                </div>
              )}
              {subdomainIsActive && currAddress && (
                <div className={classes.addressesContact} id='opened-balloon-title'>
                  <div className={classes.addressesLabel}>
                    {activeLanguage === 1 ? 'Адрес ресторана' : 'Brasserie Lambic address'}
                  </div>
                  <div>
                    {activeLanguage === 1
                      ? currAddress?.description
                      : currAddress?.description_en || currAddress?.description}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
